/**
 * Whether a worker is billed below what they are paid. A margin loss the reader should see, and never a
 * reason to refuse a save or a submit, so it is a warning rather than a schema issue: zod reports issues
 * as errors, and an error blocks the form.
 */
import { WORKER_TYPE, type WorkerFormData as Worker } from '@/lib/schemas'
import { ENGAGEMENT_BILLING_TYPE, ENGAGEMENT_TYPE } from '@/lib/constants/engagement-codes'
import { RATE_UNIT } from '@/lib/constants/work-order-codes'

export const BILL_RATE_BELOW_PAY_WARNING = "Warning: Bill rate is below one or more of the worker's billable rates"

/** What outside the worker the warning depends on. */
export interface BillRateWarningContext {
  billingType?: string
  engagementType?: string
  /** Engagement (billing) currency. Bill Rate is always in it. */
  currency?: string | null
  canViewPayRates: boolean
}

/**
 * Every pay rate of the worker's that lines up with the Bill Rate. Empty when none does.
 *
 * Salaried workers carry an hourly Reporting Pay Rate with no unit of its own, so it only lines up with
 * an hourly Bill Rate. Regular workers are compared on every billable rate they hold in the Bill Rate's
 * unit, premiums included: an overtime rate above the Bill Rate is a margin loss too. Non-billable rates
 * are left out, their hours never reaching an invoice. A worker holding no rates at all predates the rate
 * list, and the flat field is all there is.
 */
const comparablePayRates = (worker: Worker): number[] => {
  const billRateUnit = worker.billRateUnit || RATE_UNIT.HOUR

  if (worker.type === WORKER_TYPE.T4_SALARIED) {
    return billRateUnit === RATE_UNIT.HOUR && worker.reportingPayRate != null ? [worker.reportingPayRate] : []
  }
  if (worker.type !== WORKER_TYPE.REGULAR) {
    return []
  }

  const rates = worker.payRates ?? []
  if (rates.length > 0) {
    // A rate carrying no billability is saved as billable.
    return rates
      .filter(rate => rate.billable !== false && billRateUnit === (rate.payRateUnitCode || RATE_UNIT.HOUR))
      .map(rate => rate.payRate)
      .filter((payRate): payRate is number => payRate != null)
  }

  return worker.payRate != null && billRateUnit === (worker.payRateUnit || RATE_UNIT.HOUR) ? [worker.payRate] : []
}

/**
 * The warning to show under a worker's Bill Rate, or undefined when there is none. Equal rates never
 * warn; only a Bill Rate strictly below one of the worker's pay rates does.
 *
 * Makes no claim when:
 *  - the engagement carries no Bill Rate (milestone billing, internal engagements);
 *  - the user cannot view pay rates, since the pay side is redacted and naming the loss would leak it;
 *  - the worker is removed, having no ongoing billing;
 *  - the Bill Rate is blank or zero, which the required-field rule already flags;
 *  - the worker is paid in a currency other than the one the engagement bills in, or in another unit,
 *    since raw numbers across either say nothing about the margin.
 */
export const billRateWarning = (worker: Worker, context: BillRateWarningContext): string | undefined => {
  const billsForHours =
    context.billingType !== ENGAGEMENT_BILLING_TYPE.MILESTONE && context.engagementType !== ENGAGEMENT_TYPE.INTERNAL

  if (!billsForHours || !context.canViewPayRates || worker.isRemoved || !worker.billRate || worker.billRate <= 0) {
    return undefined
  }

  // A blank paymentCurrencyCode means "use the engagement currency", so the pair is comparable.
  const isSameCurrency = !worker.paymentCurrencyCode
    || worker.paymentCurrencyCode.toUpperCase() === context.currency?.toUpperCase()
  if (!isSameCurrency) {
    return undefined
  }

  const billRate = worker.billRate
  return comparablePayRates(worker).some(payRate => payRate > 0 && billRate < payRate)
    ? BILL_RATE_BELOW_PAY_WARNING
    : undefined
}
